'use client';

import React, { useState, useEffect } from 'react';

import { useGlobal } from '@/contexts/GlobalContext';
import NoteStars from '@/components/common/NoteStars';
import Uniform from '@/components/common/Uniform';
import SupportingButton from '@/components/users/SupportingButton';
import TeamInvitationButton from '@/components/users/TeamInvitationButton';
import { UserModel, FootballerProfileJson } from '@/types/users';
import { TeamMemberGlobalContextModel } from '@/types/teams';
import { SupportStatus } from '@/types/supporters';
import { Status } from '@/types/teams';
import { getFilePath } from '@/utils';

interface ProfileProps {
  propsFootballer: UserModel;
  propsUserTeams: TeamMemberGlobalContextModel[] | undefined;
  propsSupportStatus?: SupportStatus;
  propsTeamMemberStatus?: Status;
}

/**
 * プロフィールコンポーネント
 * @param param0
 * @returns
 */

const Profile: React.FC<ProfileProps> = ({
  propsFootballer,
  propsUserTeams,
  propsSupportStatus,
  propsTeamMemberStatus
}) => {
  /**
   * React context
   */
  const context = useGlobal();

  /**
   * useState
   */
  const [footballerUniform, setFootballerUniform] = useState<string>(propsFootballer.uniforms[0]?.uniform_json || '');
  const [footballerProfile, setFootballerProfile] = useState<FootballerProfileJson>(
    JSON.parse(propsFootballer.footballer.footballer_json) as FootballerProfileJson
  );

  /**
   * 自分自身かどうか
   */
  const isMe = context.user?.id === propsFootballer.id;

  useEffect(() => {
    setFootballerUniform(propsFootballer.uniforms[0]?.uniform_json || '');
    setFootballerProfile(JSON.parse(propsFootballer.footballer.footballer_json) as FootballerProfileJson);
  }, [propsFootballer]);

  // チームに所属しているかどうかを判定する関数
  const isInTeam = () => {
    return propsUserTeams?.some((userTeam) => userTeam.status === Status.Approved);
  };

  const rendarTeamName = () => (
    <ul className="space-y-1">
      {propsUserTeams?.map((userTeam) =>
        userTeam.status === Status.Approved ? (
          <li key={userTeam.team.uuid}>
            <div className="flex items-center">
              {userTeam.team.team_emblem ? (
                <img
                  src={getFilePath(userTeam.team.team_emblem.file_path)}
                  alt="teamEmblem"
                  className="w-6 h-6 object-contain"
                />
              ) : (
                <div className="text-white" style={{ color: userTeam.team.color_a }}>
                  <svg
                    className="w-6 h-6"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24">
                    <path d="M12.356 3.066a1 1 0 0 0-.712 0l-7 2.666A1 1 0 0 0 4 6.68a17.695 17.695 0 0 0 2.022 7.98 17.405 17.405 0 0 0 5.403 6.158 1 1 0 0 0 1.15 0 17.406 17.406 0 0 0 5.402-6.157A17.694 17.694 0 0 0 20 6.68a1 1 0 0 0-.644-.949l-7-2.666Z" />
                  </svg>
                </div>
              )}
              <div
                className={`text-start ms-2 text-white
                  ${userTeam.team.name.length >= 1 && userTeam.team.name.length <= 10 && ' text-base'}
                  ${userTeam.team.name.length >= 11 && userTeam.team.name.length <= 15 && ' text-sm'}
                  ${userTeam.team.name.length >= 16 && ' text-sm'}
                `}>
                {userTeam.team.name}
              </div>
            </div>
          </li>
        ) : (
          <></>
        )
      )}
    </ul>
  );

  return (
    <>
      <div
        className="flex justify-center w-full bg-cover bg-center px-4 rounded-lg"
        style={{ backgroundImage: "url('/images/profile.jpg')" }}>
        <table className="table-fixed w-full border-separate border-spacing-2 py-4 lg:px-4">
          <tbody>
            <tr>
              <th className="flex justify-center mb-4">
                <Uniform propsAccountName={footballerProfile.accountName} uniformJsonString={footballerUniform} />
              </th>
              <td className="align-top pl-4">
                <div className="font-footballlog text-white py-1 w-fit text-3xl">{footballerProfile.accountName}</div>
                <div className="flex justify-center items-center w-fit">
                  <div className="font-footballlog text-white w-fit text-sm italic">
                    {footballerProfile.numberOfLogs} Notes
                  </div>
                  {Number(footballerProfile.numberOfLogs) >= 100 && (
                    <div className="m-2">
                      <NoteStars propsNumberOfLogs={Number(footballerProfile.numberOfLogs)} />
                    </div>
                  )}
                </div>
                <ul className="flex items-center font-footballlog text-base text-gray-400 py-2">
                  {footballerProfile.birthday && <li className="mr-1 last:mr-0">{footballerProfile.birthday}</li>}
                  {footballerProfile.dominantFoot && (
                    <li className="mr-1 last:mr-0 text-sm">{footballerProfile.dominantFoot}</li>
                  )}
                </ul>
                {context.isTeamPage ? (
                  <>
                    {context.team != null && context.isTeamOwner() && !isMe && (
                      <TeamInvitationButton
                        propsTeamUUID={context.team.uuid}
                        propsFootballerId={propsFootballer.id}
                        propsTeamMemberStatus={propsTeamMemberStatus}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {!isMe && propsSupportStatus && (
                      <SupportingButton
                        propsFootballerId={propsFootballer.id}
                        propsSupportStatus={propsSupportStatus}
                      />
                    )}
                  </>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-1 font-footballlog italic text-base font-normal bg-gray-500/30 rounded text-gray-300 text-center">
                Team
              </th>
              <td className="flex iteams-center justify-center">
                {isInTeam() ? (
                  rendarTeamName()
                ) : footballerProfile.teamName != null ? (
                  <span className="text-white text-base">{footballerProfile.teamName}</span>
                ) : (
                  <span className="font-footballlog text-gray-500 italic">No Data</span>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-1 font-footballlog italic text-base font-normal bg-gray-500/30 rounded text-gray-300 text-center">
                Position
              </th>
              <td className="text-center">
                {footballerProfile.positionDetail || footballerProfile.position ? (
                  <>
                    <span className="font-footballlog text-white text-lg font-semibold me-2">
                      {footballerProfile.position}
                    </span>
                    <span className="font-footballlog text-white text-sm">{footballerProfile.positionDetail}</span>
                  </>
                ) : (
                  <span className="font-footballlog text-gray-500 italic">No Data</span>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-1 font-footballlog italic text-base font-normal bg-gray-500/30 rounded text-gray-300 text-center">
                Strength
              </th>
              <td className="text-center">
                {footballerProfile.strength ? (
                  <span className="text-white text-sm whitespace-pre-line break-all">{footballerProfile.strength}</span>
                ) : (
                  <span className="font-footballlog text-gray-500 italic">No Data</span>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-1 font-footballlog italic text-base font-normal bg-gray-500/30 rounded text-gray-300 text-center">
                50m
              </th>
              <td className="text-center">
                {footballerProfile.time50m ? (
                  <span className="font-footballlog text-white text-base">{footballerProfile.time50m}s</span>
                ) : (
                  <span className="font-footballlog text-gray-500 italic">No Data</span>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-1 font-footballlog italic text-base font-normal bg-gray-500/30 rounded text-gray-300 text-center">
                100m
              </th>
              <td className="text-center">
                {footballerProfile.time100m ? (
                  <span className="font-footballlog text-white text-base">{footballerProfile.time100m}s</span>
                ) : (
                  <span className="font-footballlog text-gray-500 italic">No Data</span>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-1 font-footballlog italic text-base font-normal bg-gray-500/30 rounded text-gray-300 text-center">
                1500m
              </th>
              <td className="text-center">
                {footballerProfile.timeM1500m ? (
                  <>
                    {footballerProfile.timeM1500m && (
                      <span className="font-footballlog text-white text-base">{footballerProfile.timeM1500m}min</span>
                    )}
                    {footballerProfile.timeS1500m && (
                      <span className="font-footballlog text-white text-base">{footballerProfile.timeS1500m}s</span>
                    )}
                  </>
                ) : (
                  <span className="font-footballlog text-gray-500 italic">No Data</span>
                )}
              </td>
            </tr>
            <tr>
              <th className="py-1 font-footballlog italic text-sm font-normal bg-gray-500/30 rounded text-gray-300 text-center">
                Favorite Soccer Player
              </th>
              <td className="text-center px-1">
                {footballerProfile.favoriteFootballer ? (
                  <span className="text-white text-base">{footballerProfile.favoriteFootballer}</span>
                ) : (
                  <span className="font-footballlog text-gray-500 italic">No Data</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Profile;
